import React from 'react';
import { Card, Row, Col, Badge, Table, ListGroup } from 'react-bootstrap'; // Asegúrate de tener los imports correctos

const BarcodeProductDisplay = ({ productData }) => {
    if (!productData) return null;

    const getNutrientAlert = (value, thresholds) => {
        if (!thresholds || value === undefined || value === null || isNaN(parseFloat(value))) return null;
        const numericValue = parseFloat(value);
        if (numericValue >= thresholds.high) return { message: '🔴 High', color: 'danger' };
        if (numericValue >= thresholds.medium) return { message: '⚠️ Moderate', color: 'warning' };
        return { message: '🟢 Good', color: 'success' };
    };

    const getScoreColor = (scoreType, grade) => {
        if (!grade) return 'secondary';
        const g = String(grade).toLowerCase();
        const colors = {
            nutriscore: { a: 'success', b: 'success', c: 'warning', d: 'warning', e: 'danger' },
            nova: { 1: 'success', 2: 'info', 3: 'warning', 4: 'danger' },
            ecoscore: { a: 'success', b: 'success', c: 'warning', d: 'warning', e: 'danger' },
        };
        return colors[scoreType]?.[g] || 'secondary';
    };

    const nutrientFields = [
        { key: 'energy-kcal_100g', label: 'Energy', unit: 'kcal' },
        { key: 'fat_100g', label: 'Fat', unit: 'g', thresholds: { high: 17.5, medium: 3 } },
        { key: 'saturated-fat_100g', label: 'Saturated Fat', unit: 'g', thresholds: { high: 5, medium: 1.5 } },
        { key: 'carbohydrates_100g', label: 'Carbs', unit: 'g' }, // Cambiado para brevedad si quieres
        { key: 'sugars_100g', label: 'Sugars', unit: 'g', thresholds: { high: 22.5, medium: 5 } },
        { key: 'fiber_100g', label: 'Fiber', unit: 'g' },
        { key: 'proteins_100g', label: 'Proteins', unit: 'g' },
        { key: 'salt_100g', label: 'Salt', unit: 'g', thresholds: { high: 1.5, medium: 0.3 } },
        { key: 'sodium_100g', label: 'Sodium', unit: 'g', thresholds: { high: 0.6, medium: 0.12 } }
    ];

    return (
        <Card className="mt-3 shadow-sm bg-white">
            <Row g={0}>
                <Col md={4} className="p-3 d-flex align-items-center justify-content-center">
                    {productData.image_front_url ? (
                        <Card.Img
                            src={productData.image_front_url}
                            alt={productData.product_name || 'Product Image'}
                            style={{ objectFit: 'contain', maxHeight: '250px', maxWidth: '100%', borderRadius: '0.25rem' }}
                        />
                    ) : (
                        <div className="text-center text-muted p-3" style={{ minHeight: '150px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            No image available
                        </div>
                    )}
                </Col>
                <Col md={8}>
                    <Card.Body>
                        <Card.Title as="h4" className="text-primary mb-1">{productData.product_name || 'Unknown Product'}</Card.Title>
                        <Card.Subtitle className="mb-2 text-muted small">{productData.brands || 'N/A Brand'}</Card.Subtitle>

                        <p className="small mb-1">
                            <strong className="text-secondary">Categories:</strong> {productData.categories_tags?.map(c => c.replace(/en:|fr:/g, '').replace(/-/g, ' ')).join(', ') || productData.categories || 'N/A'}
                        </p>
                        <p className="small mb-2">
                            <strong className="text-secondary">Quantity:</strong> {productData.quantity || 'N/A'}
                        </p>

                        <div className="d-flex flex-wrap gap-2 mb-3">
                            {productData.nutriscore_grade && <Badge bg={getScoreColor('nutriscore', productData.nutriscore_grade)} className="p-2">Nutri-Score: {productData.nutriscore_grade.toUpperCase()}</Badge>}
                            {productData.nova_group && <Badge bg={getScoreColor('nova', productData.nova_group)} className="p-2">NOVA: {productData.nova_group}</Badge>}
                            {productData.ecoscore_grade && <Badge bg={getScoreColor('ecoscore', productData.ecoscore_grade)} className="p-2">Eco-Score: {productData.ecoscore_grade.toUpperCase()}</Badge>}
                        </div>

                        {productData.nutriments && (
                            <div className="mt-2">
                                <h6 className="text-secondary">Nutrition (per 100g/ml):</h6>
                                <Table striped borderless hover size="sm" responsive className="shadow-sm small">
                                    <tbody>
                                        {nutrientFields.map(item => {
                                            const value = productData.nutriments[item.key] ?? productData.nutriments[item.key.replace('_100g', '')]; // Fallback
                                            const alert = getNutrientAlert(value, item.thresholds);
                                            return (value !== undefined && value !== null) ? (
                                                <tr key={item.key}>
                                                    <td className="fw-medium">{item.label}</td>
                                                    <td>{parseFloat(value).toFixed(1)}{item.unit}</td>
                                                    <td>{alert && <Badge bg={alert.color} text={alert.color === 'warning' ? 'dark' : 'white'} pill>{alert.message}</Badge>}</td>
                                                </tr>
                                            ) : null;
                                        })}
                                    </tbody>
                                </Table>
                            </div>
                        )}

                        {(productData.ingredients_text_en || productData.ingredients_text) && (
                            <div className="mt-2">
                                <h6 className="text-secondary">Ingredients:</h6>
                                <p className="text-muted" style={{ fontSize: '0.8rem' }}>{productData.ingredients_text_en || productData.ingredients_text}</p>
                            </div>
                        )}

                        {(productData.ingredients_analysis_tags?.length > 0 || productData.additives_tags?.length > 0) && (
                            <div className="mt-2">
                                <h6 className="text-secondary">Ingredients Evaluation:</h6>
                                {productData.ingredients_analysis_tags?.length > 0 && (
                                    <ListGroup className="mb-1 small">
                                        {productData.ingredients_analysis_tags.map(tag => (
                                            <ListGroup.Item key={tag} variant={tag.includes('palm') ? 'danger' : tag.includes('veg') ? 'success' : 'warning'} className="py-1 px-2">
                                                {tag.replace(/^en:/, '').replace(/-/g, ' ')}
                                            </ListGroup.Item>
                                        ))}
                                    </ListGroup>
                                )}
                                {productData.additives_tags?.length > 0 && (
                                    <>
                                        <h6 className="mt-1 text-danger small">Additives:</h6>
                                        <ListGroup className="small">
                                            {productData.additives_tags.map(tag => (
                                                <ListGroup.Item key={tag} variant="light" className="text-danger py-1 px-2">
                                                    {tag.replace(/^en:/, '').toUpperCase()}
                                                </ListGroup.Item>
                                            ))}
                                        </ListGroup>
                                    </>
                                )}
                            </div>
                        )}
                    </Card.Body>
                </Col>
            </Row>
        </Card>
    );
};

export default BarcodeProductDisplay; 